import {
  type HistoryLocation,
  type NavigateOptions,
  type RouteIds,
  createBrowserHistory,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { create } from 'zustand';
import { postPermissions, userHasPostCap } from './Components/posts/status';
import { type UserCaps, useAuthStore, userHasCaps } from './Utility/auth/Auth';
import type { LocationChangeEventProps } from './Utility/initApp/window.util';
import { routeTree } from './routeTree.gen';

const browserHistory = createBrowserHistory({});

export const router = createRouter({
  scrollRestoration: true,
  routeTree: routeTree,
  defaultPreload: 'intent',
  history: browserHistory,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export type RouterType = typeof router;
export type RouterIds = RouteIds<RouterType['routeTree']>;

// @todo: deprecate this when tsr supports this
type ZnBrowserHistory = {
  items: HistoryLocation[];
  push: (location: HistoryLocation) => void;
  pop: () => void;
  goBack: (opts: NavigateOptions) => void;
};

const backConfig = {
  paths: ['/dash/'],
  isValidBackPath: (path: string) => {
    return backConfig.paths.some((p) => path.startsWith(p));
  },
};

export const useZnBrowserHistory = create<ZnBrowserHistory>((set, get) => ({
  items: [browserHistory.location],
  push: (location) => {
    set((state) => {
      return {
        items: [...state.items, location],
      };
    });
  },
  pop: () => {
    set((state) => {
      if (state.items.length === 1) {
        return state;
      }

      return {
        items: state.items.slice(0, state.items.length - 2),
      };
    });
  },
  goBack: (opts: NavigateOptions) => {
    const state = get();

    const currentLocation = state.items[state.items.length - 1];
    const currentParams = currentLocation?.pathname?.split('/').filter((p) => p !== '');
    let lastLocation = null;

    // intermediate back locations
    let prevCount = -1;
    let i = state.items.length - 2;
    for (i; i >= 0; i--) {
      const location = state.items[i];
      const params = location.pathname.split('/').filter((p) => p !== '');
      const isValidBackPath = backConfig.isValidBackPath(location.pathname);

      if (
        location.pathname !== currentLocation.pathname &&
        params?.length <= currentParams?.length &&
        isValidBackPath
      ) {
        lastLocation = location;
        break;
      }
      prevCount--;
    }

    if (lastLocation !== null) {
      set((_state) => {
        const newItems = _state.items.slice(0, _state.items.length + prevCount);
        return {
          items: newItems,
        };
      });

      browserHistory.go(prevCount);
      return;
    }

    // last known "To" location
    lastLocation = null;
    i = state.items.length - 2;
    prevCount = -1;

    for (i; i >= 0; i--) {
      const location = state.items[i];
      const isValidBackPath = backConfig.isValidBackPath(location.pathname);

      if (location?.pathname === opts?.to && isValidBackPath) {
        lastLocation = location;
        break;
      }
      prevCount--;
    }

    if (lastLocation !== null) {
      set((_state) => {
        const newItems = _state.items.slice(0, _state.items.length + prevCount);
        return {
          items: newItems,
        };
      });

      browserHistory.go(prevCount);
      return;
    }

    // if no last location, go to provided "To" location
    router.navigate(opts);
  },
}));

if (process.env.NODE_ENV !== 'production') {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  (window as any).useZnBrowserHistory = useZnBrowserHistory;
}

const onLocationChange = (e: CustomEvent<LocationChangeEventProps>) => {
  if (e.detail.type === 'pushstate') {
    useZnBrowserHistory.getState().push(browserHistory.location);
  }
  if (e.detail.type === 'popstate') {
    useZnBrowserHistory.getState().pop();
  }
};

window.addEventListener('locationchange', onLocationChange);

// remove event listener on unmount
window.addEventListener('beforeunload', () => {
  window.removeEventListener('locationchange', onLocationChange);
});

// common functions

/**
 * Require user to have certain capabilities
 * If no user, redirect to login
 *
 * @param caps Capabilities to check
 * @param _redirect Redirect options on failure
 */
export const requireUserCaps = (caps: UserCaps[], _redirect: Parameters<typeof redirect>[0]) => {
  const user = useAuthStore.getState()?.user;

  if (!user) {
    throw redirect({
      to: '/auth/login',
    });
  }

  const hasCaps = userHasCaps(user?.user, caps);

  if (!hasCaps) {
    throw redirect(_redirect);
  }
};

export const requireUserPostCaps = <T extends Parameters<typeof postPermissions>[0]>(
  postType: T,
  cap: keyof ReturnType<typeof postPermissions<T>>,
  _redirect: Parameters<typeof redirect>[0]
) => {
  const user = useAuthStore.getState()?.user;

  if (!user) {
    throw redirect({
      to: '/auth/login',
    });
  }

  const permissions = postPermissions(postType);
  const hasCaps = userHasPostCap(user?.user, permissions[cap]);

  if (!hasCaps) {
    throw redirect(_redirect);
  }
};

/**
 * Require admin user to be logged in
 */
export const requireAdmin = (opts?: Parameters<typeof redirect>[0]) => {
  const isAdmin = useAuthStore.getState()?.user?.isAdmin;

  if (!isAdmin) {
    throw redirect({
      to: '/auth/login',
      ...opts,
    });
  }
};

/**
 * Require user to be logged in
 */
export const requireLoggedInUser = (opts?: Parameters<typeof redirect>[0]) => {
  const userLoggedIn = useAuthStore.getState()?.user?.userExists === true;

  if (!userLoggedIn) {
    throw redirect({
      to: '/auth/login',
      ...opts,
    });
  }
};

/**
 * Require user to be logged in
 */
export const requireNonLoggedInUser = (opts?: Parameters<typeof redirect>[0]) => {
  const userLoggedIn = useAuthStore.getState()?.user?.userExists === true;

  if (userLoggedIn) {
    throw redirect({
      to: '/dash/catalog',
      ...opts,
    });
  }
};

/**
 * Require a super admin to be logged in
 */
export const requireSuperAdmin = (opts?: Parameters<typeof redirect>[0]) => {
  const isSuperAdmin = useAuthStore.getState()?.user?.isSuperAdmin;

  if (!isSuperAdmin) {
    throw redirect({
      to: '/dash/catalog',
      ...opts,
    });
  }
};
